import { CalculationStrategies, Sample } from '../../@types/samples/types';
import { AnalysisMethod } from './types';

/**
 * Which analysis a sample belongs to when choosing samples for a batch.
 * A sample qualifies for a category if it has the relevant INPUT data OR has
 * already had that analysis calculated — so input-only (not-yet-calculated)
 * samples are grouped alongside the analyzed ones.
 *
 * NOTE: we deliberately do NOT use `performed_analyses` here. The backend
 * marks a strategy "performed" even when it was skipped (e.g. Liao on a sample
 * with no Pc data nulls the result but still flags `liao`), so a sample with
 * no usable data would falsely look analyzed. Categorization is driven by real
 * input curves and real (non-null) results instead.
 */
export type AnalysisCategory =
  | 'capillary-pressure'
  | 'kr-analysis'
  | 'no-data';

export const ANALYSIS_CATEGORY_LABELS: Record<AnalysisCategory, string> = {
  'capillary-pressure': 'Capillary Pressure',
  'kr-analysis': 'Kr Analysis',
  'no-data': 'No data',
};

const hasArrayData = (arr?: (number | null)[] | null): boolean =>
  Array.isArray(arr) && arr.some(value => value != null);

/** A relative-permeability result object with at least one computed field. */
const hasRelPermResult = (block?: Record<string, number | null> | null) =>
  Boolean(block) && Object.values(block!).some(value => value != null);

/** Has capillary-pressure input (Sw/Pc curve) or a computed Liao/Lambda result. */
export const hasCapillaryData = (sample: Sample): boolean => {
  const pc = sample.input_sample_curves?.pc_model;
  return (
    hasArrayData(pc?.sw) ||
    hasArrayData(pc?.pc) ||
    isCapillaryCalculated(sample)
  );
};

/** Has relative-permeability input (water-oil / gas-oil curves) or a Kr result. */
export const hasKrData = (sample: Sample): boolean => {
  const wo = sample.input_sample_curves?.water_oil;
  const go = sample.input_sample_curves?.gas_oil;
  const woInput = wo ? Object.values(wo).some(hasArrayData) : false;
  const goInput = go ? Object.values(go).some(hasArrayData) : false;
  return woInput || goInput || isKrCalculated(sample);
};

/** Has capillary-pressure INPUT curve (Sw/Pc) — ignores computed results. */
export const hasCapillaryInput = (sample: Sample): boolean => {
  const pc = sample.input_sample_curves?.pc_model;
  return hasArrayData(pc?.sw) || hasArrayData(pc?.pc);
};

/** Has relative-permeability water-oil INPUT curves — ignores results. */
export const hasWaterOilInput = (sample: Sample): boolean => {
  const wo = sample.input_sample_curves?.water_oil;
  return wo ? Object.values(wo).some(hasArrayData) : false;
};

/** Has relative-permeability gas-oil INPUT curves — ignores results. */
export const hasGasOilInput = (sample: Sample): boolean => {
  const go = sample.input_sample_curves?.gas_oil;
  return go ? Object.values(go).some(hasArrayData) : false;
};

/**
 * Whether a sample carries the INPUT data required by the given analysis
 * methodology, so the Multi Analysis grid only lists samples it can run:
 * Liao/Brooks-Corey need Capillary Pressure input; the Kr methodologies need
 * their respective water-oil / gas-oil input curves.
 */
export const matchesMethodInput = (
  sample: Sample,
  method: AnalysisMethod
): boolean => {
  if (method === 'water-oil') return hasWaterOilInput(sample);
  if (method === 'gas-oil') return hasGasOilInput(sample);
  return hasCapillaryInput(sample);
};

/** A real (non-null) Liao/Lambda result is stored. */
const isCapillaryCalculated = (sample: Sample): boolean =>
  sample.analysis_values?.liao != null ||
  sample.analysis_values?.lambda_values != null;

/** A real relative-permeability result is stored. */
const isKrCalculated = (sample: Sample): boolean =>
  hasRelPermResult(sample.k_rel_values?.water_oil) ||
  hasRelPermResult(sample.k_rel_values?.gas_oil);

/** A real result exists for any supported analysis (Liao/Lambda or Kr). */
export const hasComputedResult = (sample: Sample): boolean =>
  isCapillaryCalculated(sample) || isKrCalculated(sample);

/**
 * Whether a sample has a real (non-null) computed result for one specific
 * correlation strategy. Used to decide which samples the correlation can
 * actually consume — deliberately NOT `performed_analyses`, which the
 * backend flags even when the calculation was skipped (e.g. Liao on a
 * sample with no Pc data), so a sample with no usable result never looks
 * available. `strategy` is the enum's string value ('liao' | 'lambda' |
 * 'water-oil' | 'gas-oil'), matching either CalculationStrategies enum.
 */
export const hasResultForStrategy = (
  sample: Sample,
  strategy: string
): boolean => {
  switch (strategy) {
    case 'liao':
      return sample.analysis_values?.liao != null;
    case 'lambda':
      return sample.analysis_values?.lambda_values != null;
    case 'water-oil':
      return hasRelPermResult(sample.k_rel_values?.water_oil);
    case 'gas-oil':
      return hasRelPermResult(sample.k_rel_values?.gas_oil);
    default:
      return false;
  }
};

const RESULT_STRATEGIES: CalculationStrategies[] = [
  CalculationStrategies.LIAO,
  CalculationStrategies.LAMBDA,
  CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY,
  CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
];

/**
 * The strategies for which the sample has a real (non-null) computed result.
 * Use this for "analyzed" badges/filters instead of `performed_analyses`,
 * which the backend also flags for skipped/failed calculations. Purely a
 * display/selection concern — it never gates loading a sample.
 */
export const analysesWithResult = (
  sample: Sample
): CalculationStrategies[] =>
  RESULT_STRATEGIES.filter(strategy => hasResultForStrategy(sample, strategy));

/** Input data (or a result) exists for any supported analysis. */
export const hasAnyAnalysisData = (sample: Sample): boolean =>
  hasCapillaryData(sample) || hasKrData(sample);

export const matchesCategory = (
  sample: Sample,
  category: AnalysisCategory
): boolean => {
  if (category === 'capillary-pressure') return hasCapillaryData(sample);
  if (category === 'kr-analysis') return hasKrData(sample);
  // 'no-data' — no input or result for any supported analysis.
  return !hasCapillaryData(sample) && !hasKrData(sample);
};

/**
 * Whether the batch analysis for this category has already been computed for
 * the sample (vs input-only, still pending calculation). Uses real results —
 * a skipped/failed calculation counts as NOT calculated. Always false for the
 * 'no-data' bucket (nothing to calculate).
 */
export const isCategoryCalculated = (
  sample: Sample,
  category: AnalysisCategory
): boolean => {
  if (category === 'capillary-pressure') return isCapillaryCalculated(sample);
  if (category === 'kr-analysis') return isKrCalculated(sample);
  return false;
};
